import { useQuery } from "@apollo/client/react";
import { GET_REQUIREMENTS, GET_STATS } from "@/graphql/queries";
import { mapToSnakeCase } from "@/graphql/mappers";
import { completionLabel } from "@/components/Badge";
import { useFilters } from "@/context/FilterContext";
import { useApp } from "@/context/AppContext";
import { DataTable } from "@/components/DataTable";
import { Pagination } from "@/components/Pagination";
import { ownerFilterLabel, ownerFilterOptions, ownerFilterValue } from "@/requirementFilters";
import type { Requirement } from "@/types";

export function RequirementsList() {
  const { filters, setFilter, clearFilters, page, pageSize } = useFilters();
  const { treeViewActive, setTreeViewActive, activeUser, navigateToCreate } = useApp();

  const { data: reqsRaw, loading: isLoading } = useQuery<any>(GET_REQUIREMENTS);
  const { data: statsRaw } = useQuery<any>(GET_STATS);

  const canCreate = activeUser?.role === "manager" || activeUser?.role === "admin" || activeUser?.role === "lead";

  const requirements: Requirement[] = reqsRaw ? mapToSnakeCase(reqsRaw.requirements) : [];
  const stats = statsRaw ? mapToSnakeCase(statsRaw.stats) : undefined;
  const ownerOptions = ownerFilterOptions(requirements);

  const filtered = requirements.filter((r: Requirement) => {
    const isInactive = (r.active_driver_count ?? 0) === 0;
    const jiraTickets = (r.tests || []).flatMap(test => test.jira_tickets || []);
    const searchText = `${r.id} ${r.title || ""} ${r.description || ""} ${jiraTickets.join(" ")}`;
    if (filters.search && !searchText.toLowerCase().includes(filters.search.toLowerCase())) return false;
    if (filters.level === "inactive") return isInactive;
    if (isInactive) return false;
    if (filters.level && r.level !== filters.level) return false;
    if (filters.discipline && r.discipline !== filters.discipline) return false;
    if (filters.completion && r.completion !== filters.completion) return false;
    if (filters.category && r.category !== filters.category) return false;
    if (filters.owner && ownerFilterValue(r.owner) !== filters.owner) return false;
    if (filters.revision && r.revision !== filters.revision) return false;
    if (filters.testCoverage === "has_tests" && (!r.tests || r.tests.length === 0)) return false;
    if (
      filters.testCoverage === "no_tests"
      && ((r.tests?.length ?? 0) > 0 || (r.driven_child_count ?? 0) > 0)
    ) return false;
    if (filters.tag === "__not_tagged__" && r.tags && r.tags.length > 0) return false;
    if (filters.tag && filters.tag !== "__not_tagged__" && !(r.tags || []).includes(filters.tag)) return false;
    return true;
  });

  const paged = filtered.slice(page * pageSize, (page + 1) * pageSize);
  const cc = stats?.completion_counts || {};

  if (treeViewActive) return null;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Page header + summary stats */}
      <div className="pt-5 px-7">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-text leading-tight">Requirements</h1>
          </div>
          <div className="flex items-center gap-2">
            {canCreate && (
              <button
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-default border text-xs font-medium cursor-pointer whitespace-nowrap transition-all duration-150 ease-in-out bg-accent text-white border-accent hover:bg-accent2 hover:border-accent2"
                onClick={() => navigateToCreate()}
              >
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-3.5 h-3.5">
                  <path d="M8 3v10M3 8h10" />
                </svg>
                New
              </button>
            )}
            <button
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-default border text-xs font-medium cursor-pointer whitespace-nowrap transition-all duration-150 ease-in-out ${treeViewActive ? "bg-accent text-white border-accent" : "bg-bg2 text-text2 border-border hover:bg-bg3 hover:text-text hover:border-text2"}`}
              onClick={() => setTreeViewActive(!treeViewActive)}
            >
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-3.5 h-3.5">
                <path d="M3 2v12M3 4h4v3H3M3 9h6v3H3" />
              </svg>
              Tree View
            </button>
          </div>
        </div>
        {/* Summary stats */}
        <div className="flex flex-wrap items-stretch mt-3">
          <div className="flex flex-col items-start px-4.5 py-2.5 border-r border-border first:pl-0 last:border-r-0">
            <div className="text-lg font-bold text-blue leading-tight">{stats?.child_count ?? 0}</div>
            <div className="text-[10px] text-text2 uppercase tracking-wide mt-px flex items-center gap-1 before:content-[''] before:w-1.5 before:h-1.5 before:rounded-full before:bg-blue before:shrink-0">Total</div>
          </div>
          <div className="flex flex-col items-start px-4.5 py-2.5 border-r border-border first:pl-0 last:border-r-0">
            <div className="text-lg font-bold text-green leading-tight">{cc.complete ?? 0}</div>
            <div className="text-[10px] text-text2 uppercase tracking-wide mt-px flex items-center gap-1 before:content-[''] before:w-1.5 before:h-1.5 before:rounded-full before:bg-green before:shrink-0">Complete</div>
          </div>
          <div className="flex flex-col items-start px-4.5 py-2.5 border-r border-border first:pl-0 last:border-r-0">
            <div className="text-lg font-bold text-yellow leading-tight">{cc.partial ?? 0}</div>
            <div className="text-[10px] text-text2 uppercase tracking-wide mt-px flex items-center gap-1 before:content-[''] before:w-1.5 before:h-1.5 before:rounded-full before:bg-yellow before:shrink-0">Partial</div>
          </div>
          <div className="flex flex-col items-start px-4.5 py-2.5 border-r border-border first:pl-0 last:border-r-0">
            <div className="text-lg font-bold text-red leading-tight">{cc.incomplete ?? 0}</div>
            <div className="text-[10px] text-text2 uppercase tracking-wide mt-px flex items-center gap-1 before:content-[''] before:w-1.5 before:h-1.5 before:rounded-full before:bg-red before:shrink-0">Incomplete</div>
          </div>
          <div className="flex flex-col items-start px-4.5 py-2.5 border-r border-border first:pl-0 last:border-r-0">
            <div className="text-lg font-bold text-text2 leading-tight">{cc.missing_tests ?? 0}</div>
            <div className="text-[10px] text-text2 uppercase tracking-wide mt-px flex items-center gap-1 before:content-[''] before:w-1.5 before:h-1.5 before:rounded-full before:bg-text2 before:shrink-0">No Tests</div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="flex items-center px-7 pt-3.5 pb-2.5 gap-2">
        <div className="relative flex-1 max-w-80 min-w-40">
          <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text2 pointer-events-none w-3.5 h-3.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
            <circle cx="6.5" cy="6.5" r="4.5" /><path d="M10 10l4 4" />
          </svg>
          <input
            type="text"
            value={filters.search}
            onChange={e => setFilter("search", e.target.value)}
            placeholder="Search requirements..."
            className="w-full py-1.5 pl-7.5 pr-2.5 bg-bg border border-border rounded-default text-text text-xs outline-none transition-colors duration-100 focus:border-accent placeholder:text-text2"
          />
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap gap-2 px-7 pb-2.5 items-center [&_select]:px-2 [&_select]:py-1 [&_select]:bg-bg [&_select]:border [&_select]:border-border [&_select]:rounded-default [&_select]:text-text [&_select]:text-[11px] [&_select]:outline-none [&_select]:transition-colors [&_select]:duration-100 [&_select]:min-w-[100px] [&_select]:h-7 [&_select:focus]:border-accent">
        <select value={filters.level} onChange={e => setFilter("level", e.target.value)} aria-label="Level">
          <option value="">All levels</option>
          {(stats?.levels || []).map((l: string) => <option key={l} value={l}>{l}</option>)}
          <option value="inactive">inactive</option>
        </select>
        <select value={filters.discipline} onChange={e => setFilter("discipline", e.target.value)} aria-label="Discipline">
          <option value="">All disciplines</option>
          {(stats?.disciplines || []).map((d: string) => <option key={d} value={d}>{d}</option>)}
        </select>
        <select value={filters.completion} onChange={e => setFilter("completion", e.target.value)} aria-label="Status">
          <option value="">All statuses</option>
          {Object.keys(cc).map(s => <option key={s} value={s}>{completionLabel(s)}</option>)}
        </select>
        <select value={filters.category} onChange={e => setFilter("category", e.target.value)} aria-label="Category">
          <option value="">All categories</option>
          {(stats?.categories || []).map((c: string) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filters.owner} onChange={e => setFilter("owner", e.target.value)} aria-label="Owner">
          <option value="">All owners</option>
          {ownerOptions.map((owner: string) => <option key={owner} value={owner}>{ownerFilterLabel(owner)}</option>)}
        </select>
        <select value={filters.testCoverage} onChange={e => setFilter("testCoverage", e.target.value)} aria-label="Test Coverage">
          <option value="">All coverage</option>
          <option value="has_tests">Has tests</option>
          <option value="no_tests">No tests</option>
        </select>
        <select value={filters.tag} onChange={e => setFilter("tag", e.target.value)} aria-label="Tag">
          <option value="">All tags</option>
          <option value="__not_tagged__">Not tagged</option>
          {(stats?.tags || []).map((t: string) => <option key={t} value={t}>{t}</option>)}
        </select>
        {(filters.level || filters.discipline || filters.completion || filters.category || filters.owner || filters.testCoverage || filters.tag) && (
          <button className="px-2.5 py-1 bg-transparent border-none text-text2 text-[11px] cursor-pointer transition-colors duration-100 ml-auto hover:text-accent" onClick={clearFilters}>Clear filters</button>
        )}
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto min-h-0">
        {isLoading ? (
          <div className="text-center p-8 text-text2">Loading requirements...</div>
        ) : (
          <DataTable items={paged} view="requirements" />
        )}
      </div>

      <Pagination totalItems={filtered.length} />
    </div>
  );
}
